import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(null);

// The login payload is not perfectly uniform across deployments: it may be wrapped in a
// `data` envelope, and booleans can arrive as strings. Read both levels, and treat only
// genuine truthy values as true so a literal "false" does not read as enrolled.
const FALSY = ['', 'false', '0', 'no', 'null', 'undefined'];
const isTrue = (v) => {
  if (typeof v === 'string') return !FALSY.includes(v.trim().toLowerCase());
  return v === true || v === 1;
};
const readField = (payload, key) => {
  if (!payload || typeof payload !== 'object') return undefined;
  if (payload[key] !== undefined) return payload[key];
  const inner = payload.data;
  if (inner && typeof inner === 'object' && inner[key] !== undefined) return inner[key];
  return undefined;
};

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      authAPI.me()
        .then(res => setUser(res.data))
        .catch(() => localStorage.clear())
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password, mfa_code) => {
    const res = await authAPI.login(email, password, mfa_code);
    const data = res.data;
    // A 200 that is not a JSON object means the request never reached the API — the SPA's
    // own index.html comes back when /api/v1 is not proxied to the platform API. Name that
    // instead of letting it fall through and read as bad credentials.
    if (!data || typeof data !== 'object') {
      throw new Error('The sign-in service returned a non-JSON response, so the admin API is not reachable at this address. Please contact an administrator.');
    }
    const enrollmentToken = readField(data, 'mfa_enrollment_token');
    // Enrollment required: no session was issued. The API sets requires_mfa as well, but
    // there is nothing to challenge against until the account is enrolled, so enrollment
    // wins. The enrollment token is handed to the caller for /auth/mfa/setup and
    // /auth/mfa/verify only — it is not a session, so it is never stored and /auth/me is
    // never called with it.
    if (isTrue(readField(data, 'mfa_enrollment_required')) || enrollmentToken) {
      if (!enrollmentToken) {
        throw new Error('Two-factor setup is required, but the server did not return a setup token. Please try again.');
      }
      return { mfa_enrollment_required: true, mfa_enrollment_token: enrollmentToken };
    }
    // Already enrolled: caller re-submits login with an mfa_code.
    if (isTrue(readField(data, 'requires_mfa'))) return { requires_mfa: true };
    // Never persist an empty token pair — that is what turned a valid 200 into a
    // "session expired" dump via the 401 interceptor.
    const access = readField(data, 'access_token');
    const refresh = readField(data, 'refresh_token');
    if (!access || !refresh) {
      throw new Error('Sign in did not return a session. Please try again.');
    }
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    const me = await authAPI.me();
    setUser(me.data);
    return { success: true };
  };

  const logout = async () => {
    try { await authAPI.logout(localStorage.getItem('refresh_token')); } catch {}
    localStorage.clear();
    setUser(null);
  };

  const hasPermission = (key) => {
    if (!user) return false;
    return user.roles?.some(r => r.permissions?.some(p => p.key === key));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, hasPermission }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
